import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import { Ratelimit } from '@upstash/ratelimit'
import { Redis } from '@upstash/redis'
import { useMediaQuery } from '@mantine/hooks'
import useWindowDimensions from '../app/components/hooks/useWindowDimensions'
import { unsubscribeUserFromEmails } from '../app/utilities/account'
import {
  ipv4Regex,
  toTrimmedString,
  isInternalReferer,
  isNullOrEmptyString,
  isNotNullOrEmptyString,
  toTrimmedStringUpperCase,
  CANADA_ISO_3166_ALPHA_2_CODE,
  BRITAIN_ISO_3166_ALPHA_2_CODE,
  getSubscriptionPriceValidUntil,
  BRITAIN_ISO_3166_ALPHA_2_CODE_ALT,
  UNITED_STATES_ISO_3166_ALPHA_2_CODE,
  EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES,
} from '../app/utilities/misc'
import {
  IS_LOCAL,
  DEBUG_MODE,
  CONTACT_EMAIL,
  IS_PRODUCTION,
  ROUTE_CODE_HOME,
  PRODUCTION_HOST,
  USE_CONTENT_SECURITY_POLICIES,
  DEFAULT_SUBSCRIPTION_PRICE_USD,
  ISO_4217_CURRENCY_CODE_US_DOLLAR,
  USE_X_CONTENT_TYPE_OPTIONS_HEADER,
  DEFAULT_SUBSCRIPTION_PRICE_CAD_HARDWARE,
  DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE,
  DEFAULT_SUBSCRIPTION_PRICE_EURO_HARDWARE,
  DEFAULT_SUBSCRIPTION_PRICE_POUND_HARDWARE,
} from '../app/utilities/config'
import { UPSTASH_API_KEY, UPSTASH_ENDPOINT } from './api/utils/config'
import { DEFAULT_CONTENT_SECURITY_POLICY } from '../app/utilities/content-security-policies'
import useFullscreen from '../app/components/hooks/useFullscreenMode'
import {
  translate,
  PRODUCT_NAME,
  JSON_LD_TYPE_HOME,
  OG_HOME_PAGE_IMAGE,
  OG_URL_HOME_ENGLISH,
  LANGUAGE_CODE_FRENCH,
  LANGUAGE_CODE_KOREAN,
  META_KEYWORDS_ENGLISH,
  LANGUAGE_CODE_SPANISH,
  getInitialPageMetadata,
  OG_TITLE_DEFAULT_ENGLISH,
  JSON_LD_URL_HOME_ENGLISH,
  TITLE_TAG_DEFAULT_ENGLISH,
  OG_HOME_PAGE_IMAGE_FRENCH,
  OG_HOME_PAGE_IMAGE_KOREAN,
  OG_HOME_PAGE_IMAGE_SPANISH,
  OG_DESCRIPTION_DEFAULT_ENGLISH,
  META_DESCRIPTION_DEFAULT_ENGLISH,
  JSON_LD_APPLICATION_ABOUT_DEFAULT,
  JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT,
  JSON_LD_SOFTWARE_APPLICATION_KEYWORDS_ENGLISH,
} from '../app/utilities/language'

const Home = dynamic(() => import('../app/components/pages/Home/Home'), {
  ssr: true,
})

const SENTRY_COMPONENT_NAME: string = 'index'

function HomePage(props: any) {
  const { width, height } = useWindowDimensions()
  const isRunningPwa: boolean = useMediaQuery(
    '(display-mode: standalone)',
    false
  )
  const { isFullscreen } = useFullscreen()

  useEffect(() => {
    window.onpopstate = () => {
      if (window.location && window.location?.toString().indexOf('?c') > -1)
        window.location.href = window?.location?.href + '&n=0'
    }
  }, [])

  useEffect(() => {
    try {
      props?.broadcastDimensions(height, width, isRunningPwa, isFullscreen)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      props?.logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [width, height])

  useEffect(() => {
    try {
      if (history?.scrollRestoration) history.scrollRestoration = 'auto'
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      props?.logSentryError(e, SENTRY_COMPONENT_NAME)
    }
  }, [])

  return (
    <div
      style={{
        display: props?.navigating ? 'none' : 'initial',
      }}
    >
      <Home
        useUnitedStatesDollar={props?.useUnitedStatesDollar}
        subscriptionButtonCurrencyText={props?.subscriptionButtonCurrencyText}
        subscriptionButtonCurrencyText_Hardware_No_Suffix_Zeroes={
          props?.subscriptionButtonCurrencyText_Hardware_No_Suffix_Zeroes
        }
        isMacOs={props?.isMacOs}
        isSafari={props?.isSafari}
        isMobile={props?.isMobile}
        isMobileSafari={props?.isMobileSafari}
        isPracticalPod={props?.isPracticalPod}
        avifCompatible={props?.avifCompatible}
        logSentryError={props?.logSentryError}
        s3LocalCurrency={props?.s3LocalCurrency}
        s3LocalSubscriptionPrice={props?.s3LocalSubscriptionPrice}
        s3USDSubscriptionPrice={props?.s3USDSubscriptionPrice}
        s3LocalSubscriptionPrice_Hardware={
          props?.s3LocalSubscriptionPrice_Hardware
        }
        s3USDSubscriptionPrice_Hardware={props?.s3USDSubscriptionPrice_Hardware}
        isMacOsSafariLimitedRender={props?.isMacOsSafariLimitedRender}
        isIOS={props?.isIOS}
        selectedLanguage={props?.selectedLanguage}
        navigateRoute={props?.navigateRoute}
        dark={props?.dark}
      />
    </div>
  )
}

const cache: Map<any, any> = new Map()
const redis: Redis = new Redis({
  url: UPSTASH_ENDPOINT,
  token: UPSTASH_API_KEY,
})
const ratelimit: any = new Ratelimit({
  //@ts-ignore
  redis: redis,
  limiter: Ratelimit.slidingWindow(9, '3 s'),
  ephemeralCache: cache,
})

export async function getServerSideProps(context: any) {
  let isBot: boolean = false
  let isBadBot: boolean = false
  let isWarmerbot: boolean = false
  let rateLimited: boolean = false
  const initialLocale: any = context?.locale
  const initialLang: string =
    isNotNullOrEmptyString(initialLocale) &&
    initialLocale?.toString().indexOf('-') > 1
      ? initialLocale.split('-')[0]
      : initialLocale
  let userAgentLowerCase: any = ''
  let initialIp: string = ''
  let initialCountryCode: string = ''
  let initialTitle: string = TITLE_TAG_DEFAULT_ENGLISH
  let metaDescription: string = META_DESCRIPTION_DEFAULT_ENGLISH
  let ogDescription: string = OG_DESCRIPTION_DEFAULT_ENGLISH
  let ogTitle: string = OG_TITLE_DEFAULT_ENGLISH
  let jsonLdSubscriptionCurrencyPrice: string = DEFAULT_SUBSCRIPTION_PRICE_USD
  let jsonLdSubscriptionCurrencyPriceValidUntil: string | null =
    getSubscriptionPriceValidUntil()
  let jsonLdCurrencyCaps: string = ISO_4217_CURRENCY_CODE_US_DOLLAR
  let jsonLdApplicationAbout: string = JSON_LD_APPLICATION_ABOUT_DEFAULT
  let jsonLdOrganizationDescription: string =
    JSON_LD_ORGANIZATION_DESCRIPTION_DEFAULT
  let jsonLdSoftwareApplicationKeywords: string =
    JSON_LD_SOFTWARE_APPLICATION_KEYWORDS_ENGLISH
  let jsonLdOrganizationContactEmail: string = CONTACT_EMAIL
  let jsonLdHomeUrl: string = JSON_LD_URL_HOME_ENGLISH
  let ogUrl: string = OG_URL_HOME_ENGLISH
  let metaKeywords: string = META_KEYWORDS_ENGLISH
  const initialRoute: number = ROUTE_CODE_HOME
  try {
    if (isNotNullOrEmptyString(context?.req?.headers['x-vercel-ip-country']))
      initialCountryCode = context?.req?.headers['x-vercel-ip-country']
        ?.toString()
        .trim()
    else initialCountryCode = UNITED_STATES_ISO_3166_ALPHA_2_CODE
  } catch (ecs: any) {
    if (DEBUG_MODE) console.log(ecs)
  }
  try {
    userAgentLowerCase = context?.req?.headers['user-agent']
      ?.toString()
      .trim()
      .toLowerCase()
  } catch (eraw) {
    if (DEBUG_MODE) console.log(eraw)
  }
  try {
    isWarmerbot = isNotNullOrEmptyString(
      context?.req?.headers['x-action-warmer']?.toString().trim()
    )
  } catch (ezar) {
    if (DEBUG_MODE) console.log(ezar)
  }
  try {
    const isFriendlyBot = (await import('../app/utilities/bots')).isFriendlyBot
    isBot = isFriendlyBot(userAgentLowerCase)
  } catch (erra) {
    if (DEBUG_MODE) console.log(erra)
  }
  try {
    if (!isWarmerbot) {
      const isMaliciousBot = (await import('../app/utilities/bots'))
        .isMaliciousBot
      isBadBot = isMaliciousBot(userAgentLowerCase)
    }
  } catch (erra) {
    if (DEBUG_MODE) console.log(erra)
  }
  try {
    if (isNotNullOrEmptyString(initialLang)) {
      const meta: any = getInitialPageMetadata(
        'Home',
        initialLang,
        initialCountryCode,
        isBot || isBadBot || isWarmerbot
      )
      initialTitle = toTrimmedString(meta?.initialTitle)
      ogTitle = toTrimmedString(meta?.ogTitle)
      metaDescription = toTrimmedString(meta?.metaDescription)
      ogDescription = toTrimmedString(meta?.ogDescription)
      jsonLdSubscriptionCurrencyPrice = toTrimmedString(
        meta?.jsonLdSubscriptionCurrencyPrice
      )
      jsonLdCurrencyCaps = toTrimmedString(meta?.jsonLdCurrencyCaps)
      jsonLdApplicationAbout = toTrimmedString(meta?.jsonLdApplicationAbout)
      jsonLdOrganizationDescription = toTrimmedString(
        meta?.jsonLdOrganizationDescription
      )
      jsonLdSubscriptionCurrencyPriceValidUntil = toTrimmedString(
        meta?.jsonLdSubscriptionCurrencyPriceValidUntil
      )
      ogUrl = toTrimmedString(meta?.ogUrl)
      jsonLdOrganizationContactEmail = toTrimmedString(
        meta?.jsonLdOrganizationContactEmail
      )
      jsonLdHomeUrl = toTrimmedString(meta?.jsonLdHomeUrl)
      metaKeywords = toTrimmedString(meta?.metaKeywords)
    }
  } catch (ecs: any) {
    if (DEBUG_MODE) console.log(ecs)
  }
  try {
    if (isNotNullOrEmptyString(context?.req?.headers['x-forwarded-for']))
      initialIp = context?.req?.headers['x-forwarded-for']?.toString().trim()
    if (!ipv4Regex.test(initialIp)) initialIp = ''
  } catch (eme: any) {
    if (DEBUG_MODE) console.log(eme)
  }
  let referer: any = context?.req?.headers?.referer
    ? context?.req?.headers?.referer
    : null
  const isNotInternalRefer: boolean = isInternalReferer(referer) == false
  context?.res?.setHeader('X-Frame-Options', 'SAMEORIGIN')
  if (!IS_LOCAL && !isBot && !isWarmerbot && isNotInternalRefer) {
    try {
      const ip = context?.req?.ip ?? '127.0.0.1'
      const { success } = await ratelimit.limit(ip)
      rateLimited = !success
    } catch (er: any) {
      if (DEBUG_MODE) console.log(er)
    }
  }
  try {
    if (isBadBot || (!isWarmerbot && isNullOrEmptyString(userAgentLowerCase))) {
      context.res.statusCode = 401
      context.res.end()
    }
    if (rateLimited) {
      context.res.statusCode = 429
      context.res.end()
    }
  } catch (za: any) {
    if (DEBUG_MODE) console.log(za)
  }
  if (isBadBot || rateLimited) {
    return {
      props: {},
    }
  } else {
    const parsedQueryString: any = context?.query
    try {
      context?.res?.setHeader(
        'Cache-Control',
        IS_PRODUCTION
          ? 'max-age=120, s-maxage=120, stale-while-revalidate=120'
          : 'max-age=2, s-maxage=2, stale-while-revalidate=2'
      )
    } catch (er) {
      if (DEBUG_MODE) console.log(er)
    }
    if (USE_CONTENT_SECURITY_POLICIES) {
      try {
        const cspHeader: string = DEFAULT_CONTENT_SECURITY_POLICY
        const contentSecurityPolicyHeaderValue: string = cspHeader
          .replace(/\s{2,}/g, ' ')
          .trim()
        context?.res?.setHeader(
          'Content-Security-Policy',
          contentSecurityPolicyHeaderValue
        )
      } catch (er) {
        if (DEBUG_MODE) console.log(er)
      }
    }
    try {
      if (USE_X_CONTENT_TYPE_OPTIONS_HEADER)
        context?.res.setHeader('X-Content-Type-Options', 'nosniff')
    } catch (ez: any) {
      if (DEBUG_MODE) console.log(ez)
    }
    const countryCodeCaps: string = isNotNullOrEmptyString(initialCountryCode)
      ? toTrimmedStringUpperCase(initialCountryCode)
      : ''
    const isEuro: boolean =
      EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES?.length > 0 &&
      EURO_CURRENCY_ISO_3166_ALPHA_2_COUNTRY_CODES?.some(
        (ec: any) => ec == countryCodeCaps
      )
    const isBritain: boolean =
      BRITAIN_ISO_3166_ALPHA_2_CODE == countryCodeCaps ||
      BRITAIN_ISO_3166_ALPHA_2_CODE_ALT == countryCodeCaps
    const isCanada: boolean = CANADA_ISO_3166_ALPHA_2_CODE == countryCodeCaps
    const isUnsubscribeRequest: boolean = isNotNullOrEmptyString(
      context?.query?.w
    )
    const isOpenFooterItemRequest: boolean =
      !isUnsubscribeRequest && isNotNullOrEmptyString(context?.query?.ds)
    if (isUnsubscribeRequest) {
      const withAES_Alternate_KEY: string = 'KFmnMAPzP!g@6Dy5HD?JSgYC9obE&m@m'
      const withAES_Alternate_IV: string = withAES_Alternate_KEY.slice(0, 16)
      const web2Email: any = toTrimmedString(parsedQueryString?.w)
      try {
        let decryptedWeb2Email: any = ''
        if (isNotNullOrEmptyString(web2Email)) {
          let cipherText: any = toTrimmedString(web2Email)
            .toString()
            .replaceAll(' ', '+')
          try {
            const Algorithm: any = 'aes-256-cbc'
            const crypto: any = require('crypto')
            cipherText = Buffer.from(cipherText, 'base64')
            const cipher = crypto.createDecipheriv(
              Algorithm,
              withAES_Alternate_KEY,
              withAES_Alternate_IV
            )
            decryptedWeb2Email = Buffer.concat([
              cipher.update(cipherText),
              cipher.final(),
            ]).toString('utf8')
          } catch (e) {
            if (DEBUG_MODE) console.log(e)
          }
        }
        if (isNotNullOrEmptyString(decryptedWeb2Email)) {
          await unsubscribeUserFromEmails(decryptedWeb2Email)
        }
      } catch (za: any) {
        if (DEBUG_MODE) console.log(za)
      }
    }
    return {
      props: {
        s3USDSubscriptionPrice: DEFAULT_SUBSCRIPTION_PRICE_USD,
        isBot: false,
        uab: isBot || isBadBot || isWarmerbot,
        ua: userAgentLowerCase,
        referer: referer,
        initialLocale: initialLocale,
        initialLang: initialLang,
        initialIp: initialIp,
        initialCountryCode: initialCountryCode,
        initialTitle: initialTitle,
        ogTitle: ogTitle,
        metaDescription: metaDescription,
        ogDescription: ogDescription,
        jsonLdSubscriptionCurrencyPrice: jsonLdSubscriptionCurrencyPrice,
        jsonLdSubscriptionCurrencyPriceValidUntil:
          jsonLdSubscriptionCurrencyPriceValidUntil,
        jsonLdCurrencyCaps: jsonLdCurrencyCaps,
        jsonLdApplicationAbout: jsonLdApplicationAbout,
        jsonLdApplicationAboutName: PRODUCT_NAME,
        jsonLdOrganizationDescription: jsonLdOrganizationDescription,
        jsonLdPriceValidUntil: getSubscriptionPriceValidUntil(),
        jsonLdPrice: jsonLdSubscriptionCurrencyPrice,
        jsonLdSubscriptionCurrencyPrice_Hardware: isEuro
          ? DEFAULT_SUBSCRIPTION_PRICE_EURO_HARDWARE
          : isCanada
          ? DEFAULT_SUBSCRIPTION_PRICE_CAD_HARDWARE
          : isBritain
          ? DEFAULT_SUBSCRIPTION_PRICE_POUND_HARDWARE
          : DEFAULT_SUBSCRIPTION_PRICE_USD_HARDWARE,
        openFooterItem: isOpenFooterItemRequest
          ? toTrimmedString(context?.query?.ds)
          : null,
        initialRoute: initialRoute,
        ogPageImage: translate(
          OG_HOME_PAGE_IMAGE,
          OG_HOME_PAGE_IMAGE_SPANISH,
          OG_HOME_PAGE_IMAGE_FRENCH,
          OG_HOME_PAGE_IMAGE_KOREAN,
          initialLang
        ),
        ogUrl: ogUrl,
        canonicalUrl: translate(
          PRODUCTION_HOST,
          PRODUCTION_HOST + '/' + LANGUAGE_CODE_SPANISH,
          PRODUCTION_HOST + '/' + LANGUAGE_CODE_FRENCH,
          PRODUCTION_HOST + '/' + LANGUAGE_CODE_KOREAN,
          initialLang
        ),
        jsonLdSoftwareApplicationKeywords: jsonLdSoftwareApplicationKeywords,
        jsonLdOrganizationContactEmail: jsonLdOrganizationContactEmail,
        jsonLdHomeUrl: jsonLdHomeUrl,
        jsonLdType: JSON_LD_TYPE_HOME,
        metaKeywords: metaKeywords,
        hasInvitationToken: false,
        confirmedAcceptance: null,
      },
    }
  }
}

HomePage.displayName = 'HomePage'

export default HomePage
